import IncompleteIcon from "@/components/icons/chapters/IncompleteIcon";
import CompleteIcon from "@/components/icons/CompleteIcon";
import Skeleton from "@/components/Skeleton/Skeleton";
import { useDashboardYear, useYearRange } from "@/pages/dashboard/hooks/useDateRange";
import { UserTypeContext } from "@/providers/UserTypeProvider";
import { CalendarOutlined } from "@ant-design/icons";
import { Select, SelectProps, Space } from "antd";
import React, { useContext } from "react";
import CalculatingIcon from "../icons/chapters/CalculatingIcon";
import ErrorIcon from "../icons/chapters/ErrorIcon";
import styles from "./YearSelector.module.scss";

export interface YearStatus {
  year: number;
  status?: "complete" | "incomplete" | "calculating" | "error";
  disabled?: boolean;
  extraIcon?: React.ReactNode;
}

const labelRender: SelectProps["labelRender"] = (props) => {
  const { label } = props;

  if (label) {
    return (
      <Space>
        <span>
          <CalendarOutlined />
        </span>
        <span>{label}</span>
      </Space>
    );
  }
};

function YearSelector({
  disableYears = false,
  optionData,
  isLoading,
  right,
  onChange: parentOnChange,
}: {
  disableYears?: boolean;
  optionData?: YearStatus[];
  isLoading?: boolean;
  right?: React.ReactNode;
  onChange?: (year: number) => void;
}) {
  const { userType } = useContext(UserTypeContext);
  const isInternalUser = userType === "internal";
  const [open_, setOpen] = React.useState(false);
  const { year: selectedYear, updateYear } = useDashboardYear();

  const onChange = (year: number) => {
    parentOnChange?.(year);
    updateYear(year);
  };

  const { data: years, isLoading: yearsLoading } = useYearRange(isInternalUser);

  const data: YearStatus[] | undefined = optionData || years?.map((x) => ({ year: x }));

  return (
    <div className={`${styles.container}`}>
      <div
        className={`${styles.innerContainer} ${disableYears ? styles.disabled : ""} ${
          styles.leftContainer
        } ${open_ ? styles.dropdownOpen : ""}`}
        onClick={() => !open_ && setOpen(true)}
      >
        {isLoading || (yearsLoading && isInternalUser) ? (
          <Skeleton style={{ width: 75, height: 24 }} />
        ) : (
          <Select
            disabled={disableYears}
            labelRender={labelRender}
            open={open_}
            onOpenChange={(open) => setOpen(open)}
            options={data}
            style={{ width: 75 }}
            fieldNames={{ label: "year", value: "year" }}
            onChange={onChange}
            value={selectedYear}
            variant={"borderless"}
            size={"small"}
            suffixIcon={false}
            className={styles.yearSelector}
            classNames={{ popup: { root: styles.yearSelectorDropdown } }}
            optionRender={(option) => (
              <span className={styles.yearOption}>
                <span className={styles.year}>{option.data.year}</span>
                {(option.data.status || option.data.extraIcon) && (
                  <span className={styles.status}>
                    {option.data.extraIcon}
                    {option.data.status === "complete" ? (
                      <CompleteIcon />
                    ) : option.data.status === "incomplete" ? (
                      <IncompleteIcon />
                    ) : option.data.status === "error" ? (
                      <ErrorIcon />
                    ) : option.data.status === "calculating" ? (
                      <CalculatingIcon />
                    ) : (
                      <></>
                    )}
                  </span>
                )}
              </span>
            )}
          />
        )}
      </div>
      {right && <div className={`${styles.innerContainer} ${styles.rightContainer}`}>{right}</div>}
    </div>
  );
}

export default YearSelector;
